import { createStore, get, set, del, keys } from "idb-keyval";
import { getActiveClientId } from "@/lib/active-client";

/**
 * Binary blob store backed by a dedicated `idb-keyval` database.
 *
 * The default `idb-keyval` store is already shared by feature state
 * (handshake strokes, stress-test placements, asset locker entries,
 * snapshot meta lists). Mixing large `Blob` payloads into that same
 * keyspace would (a) bloat exports / debugging dumps, and (b) make
 * prefix scans noisy. We isolate blobs in a sibling database
 * (`xaes-blobs` / object-store `blobs`) so meta lookups stay cheap
 * and the blob layer can be wiped independently if a workshop ever
 * needs a hard reset.
 *
 * Key convention is `snap:${kind}:${workshopId}:${uuid}` — see
 * `useSnapshot.ts` in each WS4 facilitation feature.
 *
 * Multi-client (multi-client plan): blob bytes are client data, so with
 * the flag on every stored key gets an `:<clientId>` suffix — applied
 * HERE, transparently, so callers (and the `blobKey` strings persisted
 * inside the already-scoped store maps) keep using base keys. This is
 * the contamination barrier: a `blobKey` cached under client A resolves
 * against A's namespace only, so B's session can never load A's bytes.
 */

const STORE = createStore("xaes-blobs", "blobs");

/** The stored key for a caller-facing base key (pass-through, flag off). */
function storedKey(key: string): string {
  const clientId = getActiveClientId();
  return clientId ? `${key}:${clientId}` : key;
}

export async function saveBlob(key: string, blob: Blob): Promise<void> {
  await set(storedKey(key), blob, STORE);
}

export async function loadBlob(key: string): Promise<Blob | undefined> {
  const value = await get<Blob | undefined>(storedKey(key), STORE);
  if (value instanceof Blob) return value;
  return undefined;
}

export async function deleteBlob(key: string): Promise<void> {
  try {
    await del(storedKey(key), STORE);
  } catch {
    // Best-effort: we may be evicting a key that never landed (e.g.
    // saveBlob failed). Swallow so callers can fire-and-forget.
  }
}

/**
 * List every key in the blob store that starts with `prefix`. Used
 * by the snapshot history layer for orphan-sweep / debug tooling —
 * not on the hot path. The whole keyspace is scanned client-side
 * because `idb-keyval` doesn't expose a native range query.
 *
 * Returns BASE keys (client suffix stripped) so callers can round-trip
 * them through loadBlob/deleteBlob; keys outside the active client's
 * namespace are excluded.
 */
export async function listBlobKeys(prefix: string): Promise<string[]> {
  const clientId = getActiveClientId();
  const suffix = clientId ? `:${clientId}` : "";
  const all = await keys(STORE);
  const out: string[] = [];
  for (const k of all) {
    if (typeof k !== "string" || !k.startsWith(prefix)) continue;
    if (suffix) {
      if (k.endsWith(suffix)) out.push(k.slice(0, -suffix.length));
    } else {
      out.push(k);
    }
  }
  return out;
}
